import { ButtonOutline } from "@/_old/components/inputs/button-outline";
import { Button } from "@/_old/components/ui/button";
import { useErrorTrack } from "@/_old/features/analytics/hooks/error-track/error-track";
import { resendEmailVerificationApi } from "@/_old/features/authentication/adapters/resend-email-verification";
import { removeAllTokens } from "@/core/components/auth/services/cookies";
import { cookieManager } from "@/_old/utils/cookie-manager";
import { cn } from "@/_old/utils/utils";
import { CloseIcon } from "@woba/ds-core/icons";
import { Modal } from "@woba/ds-web";
import { AxiosError } from "axios";
import { Loader2, Mail } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { clearAllChatData } from "@/core/utils/chatbot-storage";

interface ModalErrorRequestProps {
  error?: Error | AxiosError;
}

const actualDate = new Date();
const expirationDate = new Date(
  new Date(actualDate).setMonth(actualDate.getMonth() + 1),
);

const isAuthenticationError = (error: any): boolean => {
  if (error?.__CANCEL__ && error.message === "user not logged") {
    return true;
  }

  if (error instanceof AxiosError) {
    return error.response?.status === 401;
  }

  return error?.message === "user not logged";
};

const isUserNotVerified = (error: any): boolean => {
  if (error?.message === "user not verified") {
    return true;
  }

  return false;
};

const isJsException = (error: any): boolean => {
  if (!error) return false;
  const jsErrorNames = [
    "TypeError",
    "ReferenceError",
    "SyntaxError",
    "RangeError",
    "EvalError",
    "URIError",
  ];
  return jsErrorNames.includes(error.name);
};

export const ModalErrorRequest = ({ error }: ModalErrorRequestProps) => {
  const [translate] = useTranslation(["core"]);
  const { onErrorView, onErrorClick } = useErrorTrack();
  const [showReloadButton, setShowReloadButton] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleReloadPage = () => {
    onErrorClick("CLICK_ERROR_REQUEST_BUTTON_RELOAD", "ERROR", {
      button: "reload",
    });
    window.location.reload();
  };

  const handleResendEmail = async () => {
    try {
      onErrorClick("CLICK_ERROR_REQUEST_BUTTON_RESEND_EMAIL", "ERROR", {
        button: "resend-email",
      });

      setIsLoading(true);

      const { data } = await resendEmailVerificationApi();

      if (!data) {
        throw new Error("resend email error");
      }

      toast.success(translate("modal-error-request-success-resend-email"));
      setShowReloadButton(true);
    } catch (error) {
      toast.error(translate("modal-error-request-error-resend-email"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginRedirect = () => {
    onErrorClick("CLICK_ERROR_REQUEST_BUTTON_LOGIN", "ERROR", {
      button: "login",
    });
    // Migration to query params: use redirect_path instead of cookie
    // "my-privateoffice" is a special value that web-authentication handles
    const signinUrl = new URL(`${import.meta.env.VITE_PORTAL_URL}/signin`);
    signinUrl.searchParams.set("redirect_path", "my-privateoffice");
    cookieManager.set({
      cookieName: "signupCurrentStep",
      value: 0,
      globally: true,
      expirationDate,
    });

    removeAllTokens();

    // Limpar estado do chat da Lia (localStorage e sessionStorage)
    clearAllChatData();

    window.location.href = signinUrl.toString();
  };

  const getErrorMessage = () => {
    if (!error) return translate("modal-error-request-description");

    if (isAuthenticationError(error)) {
      onErrorView("PAGE_ERROR_REQUEST_AUTH_ERROR");
      return translate("modal-error-request-auth-error");
    }

    if (isUserNotVerified(error)) {
      onErrorView("PAGE_ERROR_REQUEST_USER_NOT_VERIFIED");
      return translate("modal-error-request-user-not-verified");
    }

    if (isJsException(error)) {
      onErrorView("PAGE_ERROR_REQUEST_JS_EXCEPTION");
      return translate("modal-error-request-description");
    }

    if (error instanceof AxiosError) {
      if (error.response?.status === 403) {
        onErrorView("PAGE_ERROR_REQUEST_FORBIDDEN");
        return translate("modal-error-request-forbidden");
      }
      if (error.response?.status === 404) {
        onErrorView("PAGE_ERROR_REQUEST_NOT_FOUND");
        return translate("modal-error-request-description");
      }
      if (error.response?.status === 500) {
        onErrorView("PAGE_ERROR_REQUEST_SERVER_ERROR");
        return translate("modal-error-request-description");
      }
    }

    return error.message || translate("modal-error-request-description");
  };

  const isAuthError = isAuthenticationError(error);
  const isJsError = isJsException(error);
  const isUserNotVerifiedError = isUserNotVerified(error);

  return (
    <Modal
      trigger={<></>}
      open={true}
      title={<></>}
      description={<></>}
      css={{
        padding: 0,
        width: "100%",
        maxWidth: 488,
      }}
      maxWidth={"md"}
      closeIcon={false}
    >
      <div className="flex flex-col justify-center items-center p-6 text-center">
        <div
          className={cn(
            "flex justify-center items-center mb-4 w-16 h-16 rounded-full border-4 bg-danger-200 border-danger-700",
          )}
        >
          <CloseIcon className="w-6 h-6 text-center text-danger-700" />
        </div>
        <h2 className="text-[32px] font-bold text-neutral-900">
          {isAuthError
            ? translate("modal-error-request-title")
            : isJsError
              ? translate("modal-error-request-title", {
                  errorName: error?.name,
                })
              : isUserNotVerifiedError
                ? translate("modal-error-request-title-user-not-verified")
                : error instanceof AxiosError && error.response?.status === 403
                  ? translate("modal-error-request-title-forbidden")
                  : translate("modal-error-request-title")}
        </h2>
        <p
          className="py-6 text-base text-neutral-500"
          dangerouslySetInnerHTML={{ __html: getErrorMessage() }}
        ></p>
        {error?.stack && process.env.NODE_ENV === "development" && (
          <pre className="overflow-auto p-4 w-full max-h-32 text-xs text-left rounded bg-neutral-100">
            {error.stack}
          </pre>
        )}
      </div>
      <div className="p-6 flex flex-col gap-[6px] justify-center items-center w-full">
        {isAuthError ? (
          <ButtonOutline
            className="!w-full !bg-complementary-500 !text-neutral-900 !border !border-neutral-900"
            onClick={handleLoginRedirect}
          >
            {translate("modal-error-request-button-login")}
          </ButtonOutline>
        ) : isUserNotVerifiedError ? (
          <>
            {showReloadButton ? (
              <ButtonOutline className="!w-full" onClick={handleReloadPage}>
                {translate("modal-error-request-button-reload")}
              </ButtonOutline>
            ) : (
              <Button
                variant="green"
                className="!w-full"
                onClick={handleResendEmail}
              >
                {isLoading ? (
                  <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                ) : (
                  <Mail className="mr-2 w-5 h-5" />
                )}
                {translate("modal-error-request-button-resend-email")}
              </Button>
            )}
          </>
        ) : (
          <ButtonOutline className="!w-full" onClick={handleReloadPage}>
            {translate("modal-error-request-button-reload")}
          </ButtonOutline>
        )}
      </div>
    </Modal>
  );
};
